import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ApiModelStrategy, ApiSummaryChartOut, ApiSummarySum, AvailableChartCurrenciesEnum, AvailableChartPeriodsEnum, ChartPointTyped, ManagementTypesEnum, PriceMeasurements, SummaryChartInput } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { SelectItem, SelectItemTyped } from 'src/app/shared/dropdown/dropdown';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { LoggedUser } from 'src/app/services/authentication/authentication';
import { ModalService } from 'src/app/services/modal/modal.service';
import * as dayjs from 'dayjs';
import { distinctUntilChanged, catchError } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { getDepositOrWithdrawValue } from 'src/app/shared/functions';
import { TranslateService } from '@ngx-translate/core';
import { hooverTypes } from 'src/app/shared/charts/custom-hover-tooltip';
import { SignalsService } from 'src/app/services/signals.service';
import { TranslatedConstantsService } from 'src/app/services/translated-constants.service';
import { AcceptEfektikaNextAction, EfektikaClientService } from 'src/app/services/efektika-client.service';

export const investmentFragmentId = 'js-investments-fragment';

@Component({
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false
})
export class SummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  public preloaders = {
    summary: true,
    modelStrategies: true,
    strategiesAndDrafts: true, // same call as summary
    chart: true,
  }

  public errors = {
    summary: false,
    chart: false,
    modelStrategies: false,
    strategiesAndDrafts: false,
  }

  public summaryApiData: ApiSummarySum | null = null;
  public modelStrategies: ApiModelStrategy[] | null = null;
  public ownedStrategies: ExpectedOwnedStrategy[] | null = null;

  public currencyFilter: SelectItemTyped<AvailableChartCurrenciesEnum>[] =
    this.translatedConstantsService.getTranslatedCurrencies()

  public filtrablePeriods: SelectItemTyped<AvailableChartPeriodsEnum>[] =
    this.translatedConstantsService.getTranslatedPeriods()

  public availableInvestment: SelectItem[] = [];

  public chartValues: ChartPointTyped[] = [];
  public chartLabels: Date[] = [];
  public deposits: ChartPointTyped[] = [];
  public depositsLinePoints: ChartPointTyped[] = [];

  public chartForm!: UntypedFormGroup;

  private formSubscription?: Subscription;
  private dataRefetchSub?: Subscription;

  public disableInvestmentCreation = false;
  private observer!: IntersectionObserver;
  private investmentsHeaderLinkClickedSub?: Subscription;
  @ViewChild('investmentsSection', { static: false }) public investmentsSection!: ElementRef;
  private efektikaDraftAcceptModalChaningSub?: Subscription;

  public constructor(
    private restApiService: RestApiService,
    private loggerService: LoggerService,
    public toastService: ToastNotificationService,
    private userService: UserService,
    private modalService: ModalService,
    private router: Router,
    private translate: TranslateService,
    private signalsService: SignalsService,
    private translatedConstantsService: TranslatedConstantsService,
    private efektikaClientService: EfektikaClientService,
  ) { }

  public get positiveYield(): boolean {
    if(!this.summaryApiData) {
      return false;
    }
    return parseInt(this.summaryApiData.actualYield + '') > 0;
  }

  public get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  public ngOnInit(): void {
    if(this.loggedUser) {
      this.initData();
      this.initForm();
      this.initDataRefetchSub();
      this.disableInvestmentCreation = !this.loggedUser.validContracts.find(contract => !contract.inLiquidation);
      this.handleEfektikaModalsChainingStream();
    }
  }

  private handleEfektikaModalsChainingStream() {
    this.efektikaDraftAcceptModalChaningSub = this.efektikaClientService.acceptingEfektikaConfirmAction$
      .subscribe(({action, data: draftPortfolioId}) => {

        if (action === AcceptEfektikaNextAction.MODAL_DIP_CONFIRMATION) {
          this.modalService.openEfektikaDipModal();
        }

        if (action === AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION) {
          this.modalService.openEfektikaRegularExceededModal();
        }

        if(action === AcceptEfektikaNextAction.CREATE_REQUEST && draftPortfolioId) {
          this.restApiService.acceptDraftPortfolio(draftPortfolioId).subscribe({
            next: res => this.modalService.handleDraftAccepted(res),
            error: err => this.modalService.handleAcceptDraftError(err),
          });
        }

      });
  }

  public ngAfterViewInit(): void {
    // For determining if investments are in view to highlight header link
    this.observeInvestments(this.investmentsSection.nativeElement);

    this.investmentsHeaderLinkClickedSub = this.signalsService.investmentsHeaderLinkClicked$
      .subscribe(() => this.handleScrollToInvestments());
  }

  private initDataRefetchSub(): void {
    this.dataRefetchSub = this.signalsService.summaryOwnedInvestments$.subscribe(reload => {
      if(reload) {
        this.preloaders.strategiesAndDrafts = true
        this.refetchStrategiesAndDrafts();
      }
    });
  }

  private handleScrollToInvestments(): void {
    let timeout = 100;
    // when routing from scrolled page, await angular routing event then scroll to element
    if(window.pageYOffset > 0 && !this.router.url.includes('summary')) {
      timeout =  700;
    }

    setTimeout(() => {
      const investmentsSection = this.investmentsSection.nativeElement;

      if (investmentsSection) {
        investmentsSection.scrollIntoView();
      }
    }, timeout);
  }

  private observeInvestments(investmentsSection: HTMLElement | null) {
    if (investmentsSection) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.signalsService.setInvestmentsSectionInView(true);
          } else {
            this.signalsService.setInvestmentsSectionInView(false);
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.5
        }
      );

      // Start observing investments section
      this.observer.observe(investmentsSection);
    }
  }

  private initData(): void {
    this.restApiService.getSummaryWithPortfolios().subscribe({
      error: this.handleSummaryError.bind(this),
      next: this.handleSummaryData.bind(this),
    });

    this.restApiService.getSummaryChart({}).subscribe({
      error: this.handleSummaryChartError.bind(this),
      next: this.handleSummaryChartData.bind(this),
    });

    if(this.loggedUser.managementType === ManagementTypesEnum.EXECUTION_ONLY) {
      this.restApiService.getModelStrategiesWithInstrumentsCategoriesFilter().subscribe({
        error: this.handleModelStrategiesError.bind(this),
        next: this.handleModelStrategiesData.bind(this),
      });
    }
  }

  private refetchStrategiesAndDrafts() {
    this.restApiService.getSummaryWithPortfolios().subscribe({
      error: err => this.handleSummaryError(err, true),
      next: res => this.handleSummaryData(res, true),
    });
  }

  private initForm(): void {
    this.chartForm = new UntypedFormGroup({
      showDepositsOnChart: new UntypedFormControl(true),
      priceMeasure: new UntypedFormControl(this.currencyFilter[0].value),
      period: new UntypedFormControl(this.filtrablePeriods[0].value),
      investment: new UntypedFormControl(null),
    })

    this.formSubscription = this.chartForm.valueChanges.pipe(distinctUntilChanged()).subscribe( () => this.updateChart() );
  }

  private handleSummaryChartError(error: unknown): void {
    this.preloaders.chart = false;
    this.errors.chart = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    this.toastService.unknownApiError();
  }

  private handleSummaryChartData(data: ApiSummaryChartOut): void {
    this.preloaders.chart = false;
    this.errors.chart = false;

    this.chartLabels.length = 0;
    this.chartLabels.push(...data.priceInTime.map(
      transaction => {
        return new Date(transaction.x * 1000) // unix time
    }));

    this.chartValues.length = 0;
    this.chartValues.push(...data.priceInTime.map(
      transaction => {
        return {
          x: transaction.x * 1000, // unix time
          y: this.isTwrSelected() ? transaction.y * 100 : transaction.y,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.chartValue,
        }
    }));

    this.depositsLinePoints.length = 0;
    this.depositsLinePoints.push(...data.depositsInTime.map(
      transaction => {
        return {
          x: transaction.x * 1000, // unix time
          y: this.isTwrSelected() ? transaction.y * 100 : transaction.y,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.depositsLinePoint,
        }
    }));

    this.deposits.length = 0;
    this.deposits.push(...data.moneyTransactionsInTime.map(
      transaction => {
        let depositY = transaction.sumOfDeposits;
        const correspondingDayValue = this.depositsLinePoints.find(day => day.x === transaction.x * 1000);

        if(correspondingDayValue) {
          depositY = correspondingDayValue.y;
        } else {
          const index = this.depositsLinePoints.findIndex(val => val.x >= (transaction.x * 1000));

          const closestCharValues = {
            down: this.depositsLinePoints[index - 1],
            up: this.depositsLinePoints[index],
          };

          try {
            const aproximateY = (closestCharValues.up.y + closestCharValues.down.y) / 2;
            depositY = aproximateY;
          } catch (error) {
            console.error('Catched chart error:', error);
          }
        }

        return {
          x: transaction.x * 1000,
          y: depositY,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.deposit,
        }
      })
    );
  }

  public isTwrSelected(): boolean {
    return this.chartForm.value['priceMeasure'].value === '%';
  }

  private updateChart(): void {
    this.preloaders.chart = true;

    const params: SummaryChartInput = {
      priceMeasure: this.isTwrSelected() ? PriceMeasurements.TWR : PriceMeasurements.VALUE,
    };

    const period = this.chartForm.value['period'].value as AvailableChartPeriodsEnum;

    if(period !== AvailableChartPeriodsEnum.ALL) {
      const currentDate = dayjs();

      if(period === AvailableChartPeriodsEnum.Y1 || period === AvailableChartPeriodsEnum.Y3 || period === AvailableChartPeriodsEnum.Y5) {
        params['fromDate'] = currentDate.subtract(period, 'year');
      }
      if(period === AvailableChartPeriodsEnum.YTD) {
        const startOfYear = currentDate.startOf('year');
        params['fromDate'] = currentDate.isBefore(startOfYear) ? startOfYear.subtract(1, 'year') : startOfYear;
      }
    }

    if(this.isTwrSelected()) {
      if(this.chartForm.value['showDepositsOnChart']) {
        this.chartForm.patchValue({ showDepositsOnChart: false });
        return;
      }
    }

    if(this.chartForm.value['investment'] && this.chartForm.value['investment'].value !== 'show-all-available-data') {
      this.restApiService.getInvestmentChart(params, this.chartForm.value['investment'].value).subscribe({
        error: this.handleSummaryChartError.bind(this),
        next: this.handleSummaryChartData.bind(this),
      });
    } else {
      this.restApiService.getSummaryChart(params).subscribe({
        error: this.handleSummaryChartError.bind(this),
        next: this.handleSummaryChartData.bind(this),
      });
    }
  }

  private handleSummaryError(error: unknown, refetch = false): void {
    if (!refetch) {
      this.preloaders.summary = false;
      this.errors.summary = true;
    }

    this.preloaders.strategiesAndDrafts = false;
    this.errors.strategiesAndDrafts = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    this.toastService.unknownApiError();
  }

  private handleModelStrategiesError(error: unknown): void {
    this.preloaders.modelStrategies = false;
    this.errors.modelStrategies = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleOwnedStrategiesData(
    strategies: ExpectedOwnedStrategy[],
    drafts: ExpectedOwnedStrategy[]
  ): void {
    this.preloaders.strategiesAndDrafts = false;
    this.errors.strategiesAndDrafts = false;

    try {
      this.ownedStrategies = [
        ...drafts,
        ...strategies
      ]
      this.fillDropdownData();
    } catch (error) {
      // fixme/todo - should errors.ownedStrategies be set to true with different than default unknownApiError error?
      console.error('Failed to find portfolio detail!');
      this.toastService.unknownApiError();
      this.loggerService.error(error);
    }
  }

  private fillDropdownData(): void {
    this.availableInvestment.length = 0;
    if(this.ownedStrategies) {
      this.availableInvestment.push(
      ...this.ownedStrategies
        .filter(i => !i.isDraft)
        .map(
          strategy => {
            const typePrefix = strategy.portfolioType === 'PORTFOLIO' ? this.translate.instant('global.portfolio') : this.translate.instant('global.strategy');
            return {
              value: strategy.portfolioId,
              label: `${typePrefix} "${strategy.name}"`,
            }
          }
        ));
    }
  }

  private handleModelStrategiesData(data: ApiModelStrategy[]): void {
    this.preloaders.modelStrategies = false;
    this.errors.modelStrategies = false;
    this.modelStrategies = data;
  }

  private handleSummaryData(data: ApiSummarySum, refetch = false): void {
    if (!refetch) {
      this.preloaders.summary = false;
      this.errors.summary = false;
      this.summaryApiData = data;
    }

    this.mergeStrategiesWithDrafts(data.portfolios);
  }

  private mergeStrategiesWithDrafts(strategies: ExpectedOwnedStrategy[]): void {
    this.restApiService.getClientDraftPortfoliosAndStrategies()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (drafts) => this.handleOwnedStrategiesData(strategies, drafts),
      });
  }

  public openModal(): void {
    this.modalService.openConsultModal();
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.dataRefetchSub?.unsubscribe();
    this.investmentsHeaderLinkClickedSub?.unsubscribe();
    this.observer?.disconnect();
    this.efektikaDraftAcceptModalChaningSub?.unsubscribe(); 
    // restore
    this.signalsService.setInvestmentsSectionInView(false);
  }
}
